import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const DocumentUploadModal = ({ biodata, onClose, onUpload }) => {
  const [documents, setDocuments] = useState({
    passport_copy: null,
    photo: null,
    certificates: [],
    medical_report: null,
    work_permit: null,
    insurance: null
  });
  
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const documentTypes = [
    {
      key: 'passport_copy',
      label: 'Passport Copy',
      icon: 'FileText',
      required: true,
      accept: 'image/*,.pdf',
      description: 'Clear copy of passport information page'
    },
    {
      key: 'photo',
      label: 'Passport Photo',
      icon: 'Camera',
      required: true,
      accept: 'image/*',
      description: 'Recent passport-size photograph'
    },
    {
      key: 'certificates',
      label: 'Certificates',
      icon: 'Award',
      required: false,
      accept: 'image/*,.pdf',
      multiple: true,
      description: 'Educational certificates, training documents'
    },
    {
      key: 'medical_report',
      label: 'Medical Report',
      icon: 'Heart',
      required: false,
      accept: 'image/*,.pdf',
      description: 'Medical fitness certificate'
    },
    {
      key: 'work_permit',
      label: 'Work Permit',
      icon: 'Briefcase',
      required: false,
      accept: 'image/*,.pdf',
      description: 'Previous work permits or employment documents'
    },
    {
      key: 'insurance',
      label: 'Insurance',
      icon: 'Shield',
      required: false,
      accept: 'image/*,.pdf',
      description: 'Insurance coverage documents'
    }
  ];

  const handleFileSelect = (key, files) => {
    const fileList = Array.from(files);
    
    if (key === 'certificates') {
      setDocuments(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), ...fileList]
      }));
    } else {
      setDocuments(prev => ({
        ...prev,
        [key]: fileList[0]
      }));
    }

    // Create preview URLs for images
    fileList.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrls(prev => ({
            ...prev,
            [`${key}_${key === 'certificates' ? (prev[key]?.length || 0) + index : 'single'}`]: e.target.result
          }));
        };
        reader.readAsDataURL(file);
      }
    });

    // Mock upload progress
    setUploadProgress(prev => ({ ...prev, [key]: 0 }));
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        const newProgress = (prev[key] || 0) + 20;
        if (newProgress >= 100) {
          clearInterval(interval);
          return { ...prev, [key]: 100 };
        }
        return { ...prev, [key]: newProgress };
      });
    }, 200);
  };

  const handleRemoveFile = (key, index = null) => {
    if (key === 'certificates' && index !== null) {
      setDocuments(prev => ({
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index)
      }));
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[`${key}_${index}`];
        return newUrls;
      });
    } else {
      setDocuments(prev => ({
        ...prev,
        [key]: null
      }));
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[`${key}_single`];
        return newUrls;
      });
    }
    
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[key];
      return newProgress;
    });
  };

  const handleUploadAll = () => {
    setIsUploading(true);
    
    // Mock upload process
    setTimeout(() => {
      onUpload(documents);
      setIsUploading(false);
    }, 2000);
  };

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderDocumentUpload = (docType) => {
    const files = documents[docType.key];
    const hasFiles = docType.multiple ? files?.length > 0 : files !== null;
    
    return (
      <div key={docType.key} className="border border-secondary-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <Icon name={docType.icon} size={16} className="text-primary-600" />
            </div>
            <div>
              <h4 className="font-medium text-text-primary">
                {docType.label}
                {docType.required && <span className="text-error ml-1">*</span>}
              </h4>
              <p className="text-sm text-text-secondary">{docType.description}</p>
            </div>
          </div>
          
          {!hasFiles && (
            <div>
              <input
                type="file"
                accept={docType.accept}
                multiple={docType.multiple}
                onChange={(e) => handleFileSelect(docType.key, e.target.files)}
                className="hidden"
                id={`upload-${docType.key}`}
              />
              <label
                htmlFor={`upload-${docType.key}`}
                className="btn-primary px-4 py-2 rounded-lg font-medium cursor-pointer flex items-center"
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Upload
              </label>
            </div>
          )}
        </div>
        
        {/* File List */}
        {hasFiles && (
          <div className="space-y-3">
            {docType.multiple ? (
              files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {previewUrls[`${docType.key}_${index}`] ? (
                      <Image
                        src={previewUrls[`${docType.key}_${index}`]}
                        alt={file.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-secondary-200 rounded-lg flex items-center justify-center">
                        <Icon name="FileText" size={20} className="text-secondary-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-text-primary">{file.name}</p>
                      <p className="text-sm text-text-secondary">{getFileSize(file.size)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {uploadProgress[docType.key] !== undefined && uploadProgress[docType.key] < 100 && (
                      <div className="w-16 bg-secondary-200 rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress[docType.key]}%` }}
                        />
                      </div>
                    )}
                    {uploadProgress[docType.key] === 100 && (
                      <Icon name="CheckCircle" size={16} className="text-success" />
                    )}
                    <button
                      onClick={() => handleRemoveFile(docType.key, index)}
                      className="p-1 text-error hover:bg-error-50 rounded transition-colors duration-150"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {previewUrls[`${docType.key}_single`] ? (
                    <Image
                      src={previewUrls[`${docType.key}_single`]}
                      alt={files.name}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-secondary-200 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={20} className="text-secondary-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-text-primary">{files.name}</p>
                    <p className="text-sm text-text-secondary">{getFileSize(files.size)}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {uploadProgress[docType.key] !== undefined && uploadProgress[docType.key] < 100 && (
                    <div className="w-16 bg-secondary-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[docType.key]}%` }}
                      />
                    </div>
                  )}
                  {uploadProgress[docType.key] === 100 && (
                    <Icon name="CheckCircle" size={16} className="text-success" />
                  )}
                  <button
                    onClick={() => handleRemoveFile(docType.key)}
                    className="p-1 text-error hover:bg-error-50 rounded transition-colors duration-150"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            )}
            
            {/* Add more files button for multiple uploads */}
            {docType.multiple && (
              <div>
                <input
                  type="file"
                  accept={docType.accept}
                  multiple
                  onChange={(e) => handleFileSelect(docType.key, e.target.files)}
                  className="hidden"
                  id={`upload-more-${docType.key}`}
                />
                <label
                  htmlFor={`upload-more-${docType.key}`}
                  className="btn-secondary px-3 py-2 rounded-lg font-medium cursor-pointer flex items-center text-sm"
                >
                  <Icon name="Plus" size={14} className="mr-2" />
                  Add More Files
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const requiredDocsCount = documentTypes.filter(doc => doc.required).length;
  const uploadedRequiredDocs = documentTypes.filter(doc => 
    doc.required && (doc.multiple ? documents[doc.key]?.length > 0 : documents[doc.key] !== null)
  ).length;

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1001 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Document Upload</h2>
            <p className="text-text-secondary">
              Upload documents for {biodata?.name} ({biodata?.id})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        
        {/* Progress Summary */}
        <div className="p-6 border-b border-secondary-200 bg-secondary-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-text-primary">Upload Progress</h3>
              <p className="text-sm text-text-secondary">
                {uploadedRequiredDocs} of {requiredDocsCount} required documents uploaded
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-32 bg-secondary-200 rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadedRequiredDocs / requiredDocsCount) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-text-primary">
                {Math.round((uploadedRequiredDocs / requiredDocsCount) * 100)}%
              </span>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-6">
            {documentTypes.map(renderDocumentUpload)}
          </div>
          
          {/* Upload Guidelines */}
          <div className="mt-8 bg-accent-50 border border-accent-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-accent-600 mt-0.5" />
              <div>
                <h5 className="font-medium text-accent-800">Upload Guidelines</h5>
                <ul className="text-sm text-accent-700 mt-2 space-y-1">
                  <li>• Maximum file size: 10MB per file</li>
                  <li>• Supported formats: JPG, PNG, PDF</li>
                  <li>• Ensure documents are clear and readable</li>
                  <li>• All required documents must be uploaded before submission</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-secondary-200">
          <div className="text-sm text-text-secondary">
            {uploadedRequiredDocs < requiredDocsCount && (
              <span className="text-warning">
                {requiredDocsCount - uploadedRequiredDocs} required documents missing
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="btn-secondary px-4 py-2 rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleUploadAll}
              disabled={uploadedRequiredDocs < requiredDocsCount || isUploading}
              className="btn-primary px-6 py-2 rounded-lg font-medium flex items-center disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  Save Documents
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;